import type { ComponentType } from "react";
import type { SvgIconProps } from "@mui/material/SvgIcon";
import type { AdminFeatureId } from "@equipped/core/admin/adminFeatures";
import type { AdminCapabilityId } from "@equipped/core/admin/adminCapabilities";
import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PeopleAltOutlinedIcon from "@mui/icons-material/PeopleAltOutlined";
import MailOutlineIcon from "@mui/icons-material/MailOutlineOutlined";
import LibraryBooksOutlinedIcon from "@mui/icons-material/LibraryBooksOutlined";
import ListAltOutlinedIcon from "@mui/icons-material/ListAltOutlined";
import ChatBubbleOutlineOutlinedIcon from "@mui/icons-material/ChatBubbleOutlineOutlined";
import FlagOutlinedIcon from "@mui/icons-material/FlagOutlined";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import DevicesOtherOutlinedIcon from "@mui/icons-material/DevicesOtherOutlined";
import SellOutlinedIcon from "@mui/icons-material/SellOutlined";
import BarChartOutlinedIcon from "@mui/icons-material/BarChartOutlined";
import HistoryOutlinedIcon from "@mui/icons-material/HistoryOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import BuildOutlinedIcon from "@mui/icons-material/BuildOutlined";
import QrCode2OutlinedIcon from "@mui/icons-material/QrCode2Outlined";
import InsightsOutlinedIcon from "@mui/icons-material/InsightsOutlined";
import NotificationsActiveOutlinedIcon from "@mui/icons-material/NotificationsActiveOutlined";
import MonitorHeartOutlinedIcon from "@mui/icons-material/MonitorHeartOutlined";
import ChecklistOutlinedIcon from "@mui/icons-material/ChecklistOutlined";
import PlaylistAddCheckOutlinedIcon from "@mui/icons-material/PlaylistAddCheckOutlined";
import FolderOutlinedIcon from "@mui/icons-material/FolderOutlined";
import SecurityOutlinedIcon from "@mui/icons-material/SecurityOutlined";
import CardMembershipOutlinedIcon from "@mui/icons-material/CardMembershipOutlined";

/**
 * Mirrors `apps/admin-web/src/layout/navItems.ts`'s `ADMIN_NAV_ITEMS` (same ids/order/labels, so
 * all three admins — RN `apps/admin`, CoreUI `apps/admin-web`, and this Mantis evaluation build —
 * read as "the same product" while compared side by side, docs/325/337) — a NEW, independent
 * descriptor rather than a cross-app import (different icon vocabulary: MUI icon COMPONENTS
 * instead of CoreUI's `cil*` glyph arrays, per docs/337's own recommendation to prefer
 * `@mui/icons-material` over Mantis's default Ant Design icon set for one coherent MUI stack).
 *
 * `built` marks the "half the work" v1 scope (docs/337 §"HALF THE WORK"): only People, Invites,
 * and Provider Settings are real pages here — everything else routes to `ComingSoonPage`.
 *
 * `audit` (docs/340-ADMIN-MANTIS-AND-API-IDEAS.md Part B, added after docs/337 v3's 12) has no
 * `apps/admin-web`/RN-admin twin yet — it surfaces the already-shipped `adminAudit()` client
 * method that had zero UI callers anywhere (docs/340 §2.1). `overview` (docs/340 v1 §4.1,
 * added after `audit`) is likewise mantis-only — the new LANDING destination
 * (`App.tsx`'s `index` route now points here instead of `/people`). `utilities`
 * (docs/356-ADMIN-UTILITIES-PAGE.md v0's QR slice) is likewise mantis-only — a config-gated
 * tools shelf (QR code generator today; tracked short-links / audio-combine / PDF split-merge
 * are a later slice, docs/356 §3-6). `analytics` (docs/357-ADMIN-ANALYTICS-AND-ACCESS-
 * TRACKING.md v0) is likewise mantis-only — the admin page-access beacon's own aggregate
 * view, a sibling to `usage` (provider CONTENT usage) and `audit` (admin ACTIONS) — three
 * distinct surfaces (docs/357 §2's own table). `alerts`/`monitoring`
 * (docs/347-ADMIN-ALERTS-AND-MONITORING.md v0) are likewise mantis-only — `alerts` is the
 * owner-editable threshold-rule config page, `monitoring` the live status dashboard
 * (current metric headroom + up/down + recent alert history + the per-client Cloudflare
 * cost widget) — two pages, one feature domain, kept separate the same way `usage` and
 * `audit` are two distinct surfaces over related data.
 * `get-started` (docs/384 idea #10) is another Mantis-only convenience surface; it
 * shares `overview`'s ungated feature because it only composes existing admin reads.
 *
 * `feature` (docs/358-ADMIN-FEATURE-CONFIGURATION.md v0) is the config-driven show/hide gate —
 * `DrawerContent.tsx` and `App.tsx` both filter `ADMIN_NAV_ITEMS` through
 * `useAdminFeatures().has(item.feature)` so a disabled feature's nav row AND route are neither
 * rendered nor mounted. EVERY entry must carry one (docs/358 §7's own rule — no tab escapes the
 * system); a NEW nav item added by another PR should pick (or add, in
 * `packages/core/admin/adminFeatures.ts`) the closest-matching `AdminFeatureId`.
 */
export interface AdminNavItem {
  id: string;
  route: string;
  label: string;
  icon: ComponentType<SvgIconProps>;
  built: boolean;
  feature: AdminFeatureId;
  group: AdminNavGroupId;
  /** Optional active-provider contract capability required to mount this destination. */
  requires?: AdminCapabilityId;
  /** False for frontend-only routes that are not in the Worker's tracking allow-list. */
  track?: boolean;
}

export type AdminNavGroupId =
  | "overview"
  | "people-access"
  | "content"
  | "communication"
  | "insights"
  | "monitoring"
  | "settings";

export interface AdminNavGroup {
  id: AdminNavGroupId;
  label: string;
  defaultExpanded?: boolean;
}

/** Ordered sidebar sections; item order within each section comes from `ADMIN_NAV_ITEMS`. */
export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  { id: "overview", label: "Overview", defaultExpanded: true },
  { id: "people-access", label: "People & Access" },
  { id: "content", label: "Content" },
  { id: "communication", label: "Communication" },
  { id: "insights", label: "Insights" },
  { id: "monitoring", label: "Monitoring" },
  { id: "settings", label: "Settings" },
];

export const ADMIN_NAV_ITEMS: AdminNavItem[] = [
  {
    id: "overview",
    route: "/overview",
    label: "Overview",
    icon: DashboardOutlinedIcon,
    built: true,
    feature: "overview",
    group: "overview",
  },
  {
    id: "get-started",
    route: "/get-started",
    label: "Get started",
    icon: ChecklistOutlinedIcon,
    built: true,
    feature: "overview",
    group: "overview",
    track: false,
  },
  {
    id: "people",
    route: "/people",
    label: "People",
    icon: PeopleAltOutlinedIcon,
    built: true,
    feature: "people",
    group: "people-access",
    requires: "users.list",
  },
  {
    id: "membership",
    route: "/membership",
    label: "Membership",
    icon: CardMembershipOutlinedIcon,
    built: true,
    feature: "people",
    group: "people-access",
    requires: "users.list",
    track: false,
  },
  {
    id: "bulk-operations",
    route: "/bulk-operations",
    label: "Bulk Operations",
    icon: PlaylistAddCheckOutlinedIcon,
    built: true,
    feature: "people",
    group: "people-access",
    requires: "users.list",
    track: false,
  },
  {
    id: "invites",
    route: "/invites",
    label: "Invites",
    icon: MailOutlineIcon,
    built: true,
    feature: "invites",
    group: "people-access",
    requires: "invites.list",
  },
  {
    id: "catalogs",
    route: "/catalogs",
    label: "Catalogs",
    icon: LibraryBooksOutlinedIcon,
    built: true,
    feature: "catalogs",
    group: "content",
    requires: "grants.manage",
  },
  {
    id: "manifests",
    route: "/manifests",
    label: "Manifest editor",
    icon: ListAltOutlinedIcon,
    built: true,
    feature: "manifests",
    group: "content",
  },
  {
    id: "files",
    route: "/files",
    label: "Files",
    icon: FolderOutlinedIcon,
    built: true,
    feature: "files",
    group: "content",
  },
  {
    id: "feedback",
    route: "/feedback",
    label: "Feedback",
    icon: ChatBubbleOutlineOutlinedIcon,
    built: true,
    feature: "feedback",
    group: "communication",
  },
  {
    id: "reports",
    route: "/reports",
    label: "Reports",
    icon: FlagOutlinedIcon,
    built: true,
    feature: "moderation",
    group: "communication",
  },
  {
    id: "notices",
    route: "/notices",
    label: "Notices",
    icon: NotificationsNoneOutlinedIcon,
    built: true,
    feature: "notices",
    group: "communication",
  },
  {
    id: "progress",
    route: "/progress",
    label: "Progress",
    icon: SpeedOutlinedIcon,
    built: true,
    feature: "progress",
    group: "insights",
  },
  {
    id: "devices",
    route: "/devices",
    label: "Devices",
    icon: DevicesOtherOutlinedIcon,
    built: true,
    feature: "devices",
    group: "insights",
  },
  {
    id: "provider-settings",
    route: "/provider-settings",
    label: "Provider Settings",
    icon: SellOutlinedIcon,
    built: true,
    feature: "provider-settings",
    group: "settings",
  },
  {
    id: "security",
    route: "/security",
    label: "Security",
    icon: SecurityOutlinedIcon,
    built: true,
    feature: "provider-settings",
    group: "settings",
  },
  {
    id: "usage",
    route: "/usage",
    label: "Usage",
    icon: BarChartOutlinedIcon,
    built: true,
    feature: "usage",
    group: "insights",
  },
  {
    id: "invite-attempts",
    route: "/invite-attempts",
    label: "Invite Acceptance History",
    icon: HistoryOutlinedIcon,
    built: true,
    feature: "invite-attempts",
    group: "people-access",
  },
  {
    id: "audit",
    route: "/audit",
    label: "Audit",
    icon: FactCheckOutlinedIcon,
    built: true,
    feature: "audit",
    group: "insights",
  },
  {
    id: "utilities",
    route: "/utilities",
    label: "Utilities",
    icon: BuildOutlinedIcon,
    built: true,
    feature: "utilities",
    group: "content",
  },
  {
    id: "app-links",
    route: "/app-links",
    label: "App Links",
    icon: QrCode2OutlinedIcon,
    built: true,
    feature: "app-links",
    group: "content",
    track: false,
  },
  {
    id: "analytics",
    route: "/analytics",
    label: "Analytics",
    icon: InsightsOutlinedIcon,
    built: true,
    feature: "analytics",
    group: "insights",
  },
  {
    id: "alerts",
    route: "/alerts",
    label: "Alerts",
    icon: NotificationsActiveOutlinedIcon,
    built: true,
    feature: "alerts",
    group: "monitoring",
  },
  {
    id: "monitoring",
    route: "/monitoring",
    label: "Monitoring",
    icon: MonitorHeartOutlinedIcon,
    built: true,
    feature: "monitoring",
    group: "monitoring",
  },
];
